import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { useState } from 'react';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { cn } from '@/lib/utils';
import { useCampaignEvents } from '../hooks/use-analytics';
import { EventRowsSkeleton } from './report-skeleton';
import type { CampaignEvent, EventType } from '../types/analytics-api.types';
import {
  EVENT_TYPE_OPTIONS,
  SKIP_REASON_LABEL,
  eventLabel,
  eventTone,
  namedLinkLabel,
} from '../utils/analytics-labels';
import { getAnalyticsErrorMessage } from '../utils/analytics-errors';
import { fmtDateTime, fmtNum } from '../utils/report-format';

type RecipientEventsLookupProps = {
  campaignId: string;
  from: string;
  to: string;
};

export function RecipientEventsLookup({ campaignId, from, to }: RecipientEventsLookupProps) {
  const [feedQ, setFeedQ] = useState('');
  const [feedType, setFeedType] = useState<EventType | 'all'>('all');
  const open = !!feedQ.trim() || feedType !== 'all';

  const eventsQuery = useCampaignEvents(
    campaignId,
    {
      limit: 50,
      type: feedType === 'all' ? undefined : feedType,
      address: feedQ.trim() || undefined,
      from,
      to,
    },
    open
  );

  const events = eventsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const loaded = eventsQuery.data?.pages.reduce((n, page) => n + page.pageInfo.count, 0) ?? 0;

  const close = () => {
    setFeedQ('');
    setFeedType('all');
  };

  return (
    <div className="flex flex-col gap-5">
      {!open ? (
        <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
          <div className="mb-3">
            <h3 className="text-base font-semibold text-neutral-900">Recipient events</h3>
            <p className="text-xs text-neutral-500">
              Look up what happened to one recipient. Search matches the start of an address (a
              prefix), not a word in the middle.
            </p>
          </div>
          <FilterRow feedQ={feedQ} feedType={feedType} onQuery={setFeedQ} onType={setFeedType} />
          <p className="mt-2.5 text-xs text-neutral-500">
            Nothing is loaded until you search or pick an event type. Test sends are not in this
            feed.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 p-4">
            <h3 className="text-base font-semibold text-neutral-900">Recipient events</h3>
            <div className="flex flex-wrap items-center gap-2.5">
              <FilterRow
                feedQ={feedQ}
                feedType={feedType}
                compact
                onQuery={setFeedQ}
                onType={setFeedType}
                onClearQuery={() => setFeedQ('')}
              />
              <button
                type="button"
                className="text-xs text-neutral-600 underline hover:text-neutral-900"
                onClick={close}
              >
                Close
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-500">
                    Event
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-500">
                    Recipient
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-500">
                    Detail
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-500">
                    Occurred
                  </th>
                </tr>
              </thead>
              <tbody>
                {eventsQuery.isLoading ? (
                  <EventRowsSkeleton />
                ) : eventsQuery.isError ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-10 text-center text-sm text-neutral-700">
                      {getAnalyticsErrorMessage(eventsQuery.error)}
                    </td>
                  </tr>
                ) : events.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-10">
                      <div className="mx-auto flex max-w-[420px] flex-col items-center gap-2 text-center">
                        <MagnifyingGlass className="size-[26px] text-neutral-400" aria-hidden />
                        <div className="font-medium text-neutral-900">No events found</div>
                        <p className="text-xs text-neutral-600">
                          That address has no recorded events for this campaign. The person may not
                          have been in the audience, or they may have been skipped before send.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  events.map((event) => <EventRow key={event.itemId} event={event} />)
                )}
              </tbody>
            </table>
          </div>
          {eventsQuery.hasNextPage ? (
            <div className="flex justify-center border-t border-neutral-200 p-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={eventsQuery.isFetchingNextPage}
                onClick={() => eventsQuery.fetchNextPage()}
              >
                {eventsQuery.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : events.length > 0 ? (
            <div className="border-t border-neutral-200 p-3 text-center text-xs text-neutral-500">
              End of the feed · {fmtNum(loaded)} event{loaded === 1 ? '' : 's'} shown
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function FilterRow({
  feedQ,
  feedType,
  compact,
  onQuery,
  onType,
  onClearQuery,
}: {
  feedQ: string;
  feedType: EventType | 'all';
  compact?: boolean;
  onQuery: (value: string) => void;
  onType: (value: EventType | 'all') => void;
  onClearQuery?: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div
        className={cn(
          'flex items-center gap-2 rounded-md border border-neutral-300 bg-white focus-within:border-primary-600 focus-within:ring-2 focus-within:ring-primary-600/25',
          compact ? 'px-3 py-1.5' : 'min-w-0 max-w-[420px] flex-1 px-3 py-2'
        )}
      >
        <MagnifyingGlass className="size-[15px] shrink-0 text-neutral-500" aria-hidden />
        <Input
          type="search"
          value={feedQ}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Address prefix, such as jane"
          aria-label="Find a recipient by address prefix"
          autoComplete="off"
          className={cn(
            'h-auto border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0',
            compact && 'w-[220px]'
          )}
        />
        {compact && feedQ && onClearQuery ? (
          <button
            type="button"
            className="grid size-[18px] place-items-center rounded-full hover:bg-neutral-200"
            aria-label="Clear search"
            onClick={onClearQuery}
          >
            <X className="size-[13px]" aria-hidden />
          </button>
        ) : null}
      </div>
      {compact ? null : <span className="text-xs text-neutral-500">or browse</span>}
      <select
        className="rounded-md border border-neutral-300 bg-white px-2.5 py-2 text-[13px] text-neutral-900"
        value={feedType}
        onChange={(e) => onType(e.target.value as EventType | 'all')}
        aria-label="Filter by event type"
      >
        {EVENT_TYPE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function EventRow({ event }: { event: CampaignEvent }) {
  const tone = eventTone(event);
  const named = event.type === 'click' ? namedLinkLabel(event.linkUrl) : null;
  return (
    <tr className="border-t border-neutral-100">
      <td className="px-4 py-2.5 align-top">
        <span
          className={cn(
            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
            tone === 'success' && 'bg-[#E8F7EE] text-[#0A7A3E]',
            tone === 'info' && 'bg-[#E8F1FB] text-[#0B5CAD]',
            tone === 'warning' && 'bg-[#FFF4E5] text-[#8A5B00]',
            tone === 'error' && 'bg-[#FDECEC] text-[#B3261E]',
            tone === 'neutral' && 'bg-neutral-100 text-neutral-600'
          )}
        >
          {eventLabel(event.type, event.subType)}
        </span>
      </td>
      <td className="px-4 py-2.5 align-top text-sm text-neutral-700">
        {event.contactName ? (
          <>
            <div className="font-medium text-neutral-900">{event.contactName}</div>
            <div className="text-xs text-neutral-500">{event.address}</div>
          </>
        ) : (
          event.address || '—'
        )}
      </td>
      <td className="px-4 py-2.5 align-top text-sm text-neutral-600">
        {event.type === 'click' && named ? (
          <span>
            {named}
            {event.userAgentFamily ? ` · ${event.userAgentFamily}` : ''}
          </span>
        ) : event.type === 'click' && event.linkUrl ? (
          <span>
            <a href={event.linkUrl} className="text-primary-600" target="_blank" rel="noreferrer">
              {event.linkUrl}
            </a>
            {event.userAgentFamily ? ` · ${event.userAgentFamily}` : ''}
          </span>
        ) : event.type === 'skipped' ? (
          SKIP_REASON_LABEL[event.subType || ''] || event.subType || 'Unknown reason'
        ) : (
          <span className="text-neutral-400">—</span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 align-top text-right text-xs tabular-nums text-neutral-500">
        {fmtDateTime(event.occurredAt)}
      </td>
    </tr>
  );
}
